// src/AdminOpenDrawBuyers.jsx
import * as React from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Alert, AppBar, Box, Button, Chip, Container, CssBaseline, Divider, IconButton,
  Paper, Stack, Tab, Tabs, TextField, ThemeProvider, Toolbar, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, MenuItem
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import JSZip from "jszip";
import logoNewStore from "./Logo-branca-sem-fundo-768x132.png";
import { useAuth } from "./authContext";
import { adminPanelPaperSx, createNewStoreAdminTheme } from "./adminTheme";

/* ---------- tema ---------- */
const theme = createNewStoreAdminTheme();

/* ---------- helpers de API (iguais ao AdminDashboard) ---------- */
const RAW_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_BASE ||
  "/api";
const API_BASE = String(RAW_BASE).replace(/\/+$/, "");

const apiJoin = (path) => {
  let p = path.startsWith("/") ? path : `/${path}`;
  const baseHasApi = /\/api\/?$/.test(API_BASE);
  if (baseHasApi && p.startsWith("/api/")) p = p.slice(4);
  if (!baseHasApi && !p.startsWith("/api/")) p = `/api${p}`;
  return `${API_BASE}${p}`;
};

const authHeaders = () => {
  const tk =
    localStorage.getItem("ns_auth_token") ||
    sessionStorage.getItem("ns_auth_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("token");
  return tk
    ? { Authorization: `Bearer ${String(tk).replace(/^Bearer\s+/i, "").replace(/^["']|["']$/g, "")}` }
    : {};
};

async function getJSON(path) {
  const r = await fetch(apiJoin(path), { headers: { "Content-Type": "application/json", ...authHeaders() }, credentials: "omit", cache: "no-store" });
  if (!r.ok) {
    let message = String(r.status);
    try {
      const body = await r.json();
      message = body?.message || body?.error || message;
    } catch {}
    throw new Error(message);
  }
  return r.json();
}

/* ---------- util ---------- */
const pad2 = (n) => String(n).padStart(2, "0");
const palette = [
  "#59d98e","#5bb6ff","#ffb74d","#e57373","#ba68c8","#4db6ac","#7986cb",
  "#aed581","#90a4ae","#f06292","#9575cd","#4fc3f7","#81c784","#ff8a65",
];
const buyerColor = (idx) => palette[idx % palette.length];

/* ----- helpers de imagem (logo opcional) ----- */
async function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}
async function preloadAsDataURL(src) {
  const res = await fetch(src, { cache: "no-store" });
  const blob = await res.blob();
  return blobToDataURL(blob);
}

/* ----- helpers de canvas (formas arredondadas) ----- */
function roundRectPath(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
function fillRounded(ctx, x, y, w, h, r, fill) {
  roundRectPath(ctx, x, y, w, h, r);
  ctx.fillStyle = fill;
  ctx.fill();
}
function strokeRounded(ctx, x, y, w, h, r, stroke, lw = 2) {
  roundRectPath(ctx, x, y, w, h, r);
  ctx.lineWidth = lw;
  ctx.strokeStyle = stroke;
  ctx.stroke();
}

/* ----- word wrap simples ----- */
function wrapText(ctx, text, maxWidth) {
  const words = String(text || "").split(/\s+/g);
  const lines = [];
  let line = "";
  for (let i = 0; i < words.length; i++) {
    const test = line ? line + " " + words[i] : words[i];
    if (ctx.measureText(test).width <= maxWidth) {
      line = test;
    } else {
      if (line) lines.push(line);
      line = words[i];
    }
  }
  if (line) lines.push(line);
  return lines;
}

// Converte canvas em Blob (Promise)
const canvasToBlob = (canvas, type = "image/png", quality) =>
  new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Falha ao gerar blob do canvas"));
        resolve(blob);
      }, type, quality);
    } catch (e) {
      reject(e);
    }
  });

// Faz download via URL blob
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

// Normaliza nome de arquivo (sem caracteres quebrados)
const safeFilename = (name) =>
  String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

const isOpenAdditionalItem = (item) => {
  const draw = item?.draw || {};
  const type = String(draw.draw_type || "").toLowerCase();
  return (
    String(draw.status || "").toLowerCase() === "open" &&
    (type === "adicional" || type === "secundario")
  );
};

const newestAdditionalItem = (items, predicate = () => true) =>
  items.reduce((newest, item) => {
    if (!predicate(item)) return newest;
    if (!newest || Number(item?.draw?.id || 0) > Number(newest?.draw?.id || 0)) {
      return item;
    }
    return newest;
  }, null);

const sortAdditionalItems = (items) =>
  [...items].sort((a, b) => {
    const openDifference = Number(isOpenAdditionalItem(b)) - Number(isOpenAdditionalItem(a));
    if (openDifference) return openDifference;
    return Number(b?.draw?.id || 0) - Number(a?.draw?.id || 0);
  });

export default function AdminOpenDrawBuyers() {
  const navigate = useNavigate();
  useAuth();

  const [drawMode, setDrawMode] = React.useState("principal");
  const [additionalDraws, setAdditionalDraws] = React.useState([]);
  const [selectedAdditionalDrawId, setSelectedAdditionalDrawId] = React.useState("");
  const [tab, setTab] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [drawId, setDrawId] = React.useState(null);
  const [sold, setSold] = React.useState(0);
  const [remaining, setRemaining] = React.useState(0);
  const [buyers, setBuyers] = React.useState([]);      // [{user_id, name, email, numbers[], count, total_cents}]
  const [numbers, setNumbers] = React.useState([]);    // [{n, user_id, name, email}]
  const [gridNumbers, setGridNumbers] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [exportError, setExportError] = React.useState("");
  const [emptyMessage, setEmptyMessage] = React.useState("");
  const [loadError, setLoadError] = React.useState("");
  const loadSequence = React.useRef(0);

  const load = React.useCallback(async () => {
    const sequence = ++loadSequence.current;
    setLoading(true);
    setDrawId(null);
    setSold(0);
    setRemaining(0);
    setBuyers([]);
    setNumbers([]);
    setGridNumbers([]);
    setEmptyMessage("");
    setLoadError("");
    setExportError("");

    try {
      if (drawMode === "principal") {
        const r = await getJSON("/admin/dashboard/open-buyers");
        if (sequence !== loadSequence.current) return;

        const principalTotal = Math.max(0, Number(r.total || 0));
        setDrawId(r.draw_id ?? null);
        setSold(Number(r.sold || 0));
        setRemaining(Number(r.remaining ?? Math.max(0, principalTotal - Number(r.sold || 0))));
        setBuyers(Array.isArray(r.buyers) ? r.buyers : []);
        setNumbers(Array.isArray(r.numbers) ? r.numbers : []);
        setGridNumbers(Array.from({ length: principalTotal || 100 }, (_, n) => n));
        return;
      }

      const payload = await getJSON("/admin/additional-draws");
      const items = sortAdditionalItems(Array.isArray(payload?.draws) ? payload.draws : []);
      const current =
        items.find((item) => String(item?.draw?.id) === String(selectedAdditionalDrawId)) ||
        newestAdditionalItem(items, isOpenAdditionalItem) ||
        newestAdditionalItem(items) ||
        null;

      if (sequence !== loadSequence.current) return;
      setAdditionalDraws(items);
      if (!current?.draw?.id) {
        setSelectedAdditionalDrawId("");
        setEmptyMessage("Nenhum sorteio adicional cadastrado.");
        return;
      }
      if (String(current.draw.id) !== String(selectedAdditionalDrawId)) {
        setSelectedAdditionalDrawId(String(current.draw.id));
      }

      const additionalDrawId = Number(current.draw.id);
      const numberPayload = await getJSON(`/admin/additional-draws/${additionalDrawId}/numbers`);
      if (sequence !== loadSequence.current) return;

      const allNumbers = Array.isArray(numberPayload?.numbers) ? numberPayload.numbers : [];
      const stats = current.stats || {};
      const additionalTotal = Number(stats.total_numbers ?? stats.total ?? allNumbers.length ?? 0);
      const additionalSold = Number(stats.sold_count ?? stats.sold ?? 0);
      const additionalRemaining = Number(
        stats.remaining ?? stats.available_count ?? stats.available ?? Math.max(0, additionalTotal - additionalSold)
      );
      const ticketPriceCents = Number(
        current?.config?.ticket_price_cents ?? current?.draw?.ticket_price_cents ?? 0
      );
      const additionalBuyers = (Array.isArray(current.buyers) ? current.buyers : []).map((buyer) => {
        const buyerNumbers = Array.isArray(buyer.numbers) ? buyer.numbers.map(Number) : [];
        const count = Number(buyer.numbers_count ?? buyer.count ?? buyerNumbers.length ?? 0);
        return {
          ...buyer,
          numbers: buyerNumbers,
          count,
          total_cents: Number(buyer.total_cents ?? count * ticketPriceCents),
        };
      });
      const soldNumbers = allNumbers
        .filter((item) => item?.payment_id || ["sold", "approved", "paid", "pago"].includes(String(item?.status || "").toLowerCase()))
        .map((item) => ({
          n: Number(item.n),
          user_id: item.user_id ?? null,
          name: item.user_name || null,
          email: item.user_email || null,
        }));

      setDrawId(additionalDrawId);
      setSold(additionalSold);
      setRemaining(additionalRemaining);
      setBuyers(additionalBuyers);
      setNumbers(soldNumbers);
      setGridNumbers(
        allNumbers
          .map((item) => Number(item?.n))
          .filter((n) => Number.isInteger(n) && n >= 0)
          .sort((a, b) => a - b)
      );
    } catch (error) {
      if (sequence === loadSequence.current) {
        setLoadError(
          error?.message && !/^\d+$/.test(String(error.message))
            ? String(error.message)
            : drawMode === "adicional"
            ? "Não foi possível carregar os compradores do sorteio adicional."
            : "Não foi possível carregar os compradores do sorteio principal."
        );
      }
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [drawMode, selectedAdditionalDrawId]);
  React.useEffect(() => { load(); }, [load]);

  const isAdditionalMode = drawMode === "adicional";
  const drawTypeSlug = isAdditionalMode ? "adicional" : "principal";
  const screenTitle = "Sorteio Ativo — Compradores";

  // Map de user_id -> idx/color
  const idToIdx = React.useMemo(() => {
    const ids = buyers.map(b => b.user_id);
    const map = new Map();
    let k = 0;
    ids.forEach(id => { if (!map.has(id)) { map.set(id, k++); } });
    return map;
  }, [buyers]);

  const filteredBuyers = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return buyers;
    return buyers.filter(b =>
      String(b.name || "").toLowerCase().includes(q) ||
      String(b.email || "").toLowerCase().includes(q) ||
      (Array.isArray(b.numbers) && b.numbers.some(n => pad2(n).includes(q)))
    );
  }, [buyers, query]);

  const exportCSV = () => {
    const rows = [];
    rows.push(["draw_id","user_id","name","email","count","numbers","total_cents"]);
    buyers.forEach(b => {
      rows.push([
        drawId,
        b.user_id,
        (b.name || "").replaceAll(","," "),
        (b.email || "").replaceAll(","," "),
        b.count,
        (b.numbers || []).map(pad2).join(" "),
        b.total_cents || 0
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v ?? "").replaceAll('"','""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sorteio_${drawTypeSlug}_${drawId}_compradores.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** ---------- EXPORT 1: PNG 1080x1920 (Grade) ---------- */
  const exportPNGMobile = async () => {
    if (loading) {
      alert("Aguarde carregar os dados do sorteio antes de exportar.");
      return;
    }
    const isStandardGrid =
      gridNumbers.length === 100 && gridNumbers.every((n, index) => Number(n) === index);
    if (!isStandardGrid) {
      alert("A exportação PNG da grade está disponível somente para sorteios com números de 00 a 99.");
      return;
    }

    const W = 1080, H = 1920;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    // Fundo
    ctx.fillStyle = "#0E0E0E";
    ctx.fillRect(0, 0, W, H);

    const Mx = 36, My = 48;
    let y = My;

    // Logo
    try {
      const dataURL = await preloadAsDataURL(logoNewStore);
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = dataURL; });
      const h = 72;
      const scale = h / img.height;
      const w = img.width * scale;
      ctx.drawImage(img, Mx, y, w, h);
    } catch {}
    y += 72 + 12;

    // Título
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "900 44px Inter, system-ui, Segoe UI, Roboto, Arial";
    ctx.textBaseline = "top";
    ctx.fillText(`${isAdditionalMode ? "Sorteio Adicional" : "Sorteio Ativo"} — Grade 00–99`, Mx, y);
    y += 44 + 16;

    // Metadados
    const metaGap = 24;
    const metaLabels = ["Nº Sorteio", "Vendidos", "Restantes"];
    const metaValues = [
      String(drawId ?? "-"),
      String(sold ?? 0),
      String(Math.max(0, remaining ?? (100 - (sold || 0)))),
    ];
    const colW = 280;

    for (let i = 0; i < 3; i++) {
      const x = Mx + i * (colW + metaGap);
      ctx.globalAlpha = 0.75;
      ctx.font = "700 24px Inter, system-ui, Segoe UI, Roboto, Arial";
      ctx.fillText(metaLabels[i], x, y);
      ctx.globalAlpha = 1;
      ctx.font = "900 36px Inter, system-ui, Segoe UI, Roboto, Arial";
      ctx.fillText(metaValues[i], x, y + 28);
    }
    y += 36 + 28 + 28;

    // Grade 10x10
    const gap = 10;
    const footerH = 28 + 12 + 8;
    const availH = H - y - footerH - My;
    const availW = W - Mx * 2;

    const cellW = (availW - gap * 9) / 10;
    const cellHMax = (availH - gap * 9) / 10;
    const cell = Math.min(cellW, cellHMax);
    const gridW = cell * 10 + gap * 9;
    const startX = Mx + (availW - gridW) / 2;
    const startY = y;

    // Mapa número → dono
    const ownByNum = new Map();
    numbers.forEach((x) => {
      const nNum = Number(x.n);
      const idx  = idToIdx.get(x.user_id) ?? 0;
      ownByNum.set(nNum, { idx, owner: x });
    });

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    for (let i = 0; i < 100; i++) {
      const r = Math.floor(i / 10);
      const c = i % 10;
      const x = startX + c * (cell + gap);
      const yy = startY + r * (cell + gap);

      const info = ownByNum.get(i);
      if (info) {
        fillRounded(ctx, x, yy, cell, cell, 16, buyerColor(info.idx));
        ctx.fillStyle = "#000000";
      } else {
        strokeRounded(ctx, x, yy, cell, cell, 16, "rgba(255,255,255,0.18)", 2);
        ctx.fillStyle = "#FFFFFF";
      }

      ctx.font = `${Math.round(cell * 0.36)}px Inter, system-ui, Segoe UI, Roboto, Arial`;
      ctx.fillText(pad2(i), x + cell / 2, yy + cell / 2);
    }

    // Footer
    const footY = startY + 10 * (cell + gap) + 16;
    ctx.globalAlpha = 0.8;
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "22px Inter, system-ui, Segoe UI, Roboto, Arial";
    ctx.textAlign = "left";
    ctx.fillText(`Gerado pela administração • ${new Date().toLocaleString("pt-BR")}`, Mx, footY);
    ctx.textAlign = "right";
    ctx.fillText("newstore", W - Mx, footY);
    ctx.globalAlpha = 1;

    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `sorteio_${drawTypeSlug}_${drawId}_grade_1080x1920.png`;
    a.click();
  };

  /** ---------- EXPORT 2: ZIP com PNGs 1080x1920 (Lista completa de nomes e números) ---------- */
  const exportPNGListMobile = async () => {
    try {
      setExportError("");

      if (loading) {
        setExportError("Aguarde carregar os dados do sorteio antes de exportar.");
        return;
      }

      if (!drawId) {
        setExportError("Não foi possível exportar: sorteio inválido.");
        return;
      }

      const list = Array.isArray(buyers) ? buyers : [];

      if (list.length === 0) {
        setExportError("Não há compradores para exportar.");
        return;
      }

      try {
        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready;
        }
      } catch {}

      const W = 1080;
      const H = 1920;

      const P = 56;
      const TOP_Y = 70;
      const HEADER_END_Y = 350;
      const FOOTER_H = 86;
      const CONTENT_BOTTOM = H - FOOTER_H;

      const COL_GAP = 36;
      const V_GAP = 20;
      const CARD_RADIUS = 24;

      const cardW = (W - 2 * P - COL_GAP) / 2;
      const innerPadX = 38;
      const innerW = cardW - innerPadX * 2;

      const NAME_FONT = "900 34px Inter, system-ui, Segoe UI, Roboto, Arial";
      const META_FONT = "800 25px Inter, system-ui, Segoe UI, Roboto, Arial";
      const NUM_FONT = "700 28px Inter, system-ui, Segoe UI, Roboto, Arial";

      const NAME_LH = 38;
      const META_LH = 30;
      const NUM_LH = 32;

      // Máximo de linhas de números por card.
      // Se passar disso, o mesmo comprador vira "Parte 1/2", "Parte 2/2" etc.
      // Assim não corta números e não estoura o canvas.
      const MAX_NUM_LINES_PER_CARD = 9;

      const now = new Date();
      const stamp = now.toLocaleString("pt-BR", { hour12: false });

      const mCanvas = document.createElement("canvas");
      mCanvas.width = 10;
      mCanvas.height = 10;
      const mctx = mCanvas.getContext("2d");

      const normalizeBuyer = (b, idx) => {
        const rawNums = Array.isArray(b?.numbers) ? b.numbers : [];

        const cleanNums = Array.from(
          new Set(
            rawNums
              .map((n) => Number(n))
              .filter((n) => Number.isFinite(n))
          )
        ).sort((a, z) => a - z);

        const name = String(b?.name || b?.email || "(sem nome)").trim();
        const email = String(b?.email || "").trim();

        const qty = Number(
          b?.count || cleanNums.length || 0
        );

        const colorIndex = idToIdx.get(b?.user_id) ?? idx;

        return {
          user_id: b?.user_id,
          name,
          email,
          numbers: cleanNums,
          qty,
          total_cents: Number(b?.total_cents || 0),
          accent: buyerColor(colorIndex),
        };
      };

      const wrapSafe = (ctx, text, maxWidth) => {
        const value = String(text || "").trim();
        if (!value) return [];
        return wrapText(ctx, value, maxWidth);
      };

      const fitSingleLine = (ctx, text, maxWidth) => {
        const value = String(text || "");
        if (ctx.measureText(value).width <= maxWidth) return value;

        let out = value;
        while (out.length > 3 && ctx.measureText(`${out}…`).width > maxWidth) {
          out = out.slice(0, -1);
        }
        return `${out}…`;
      };

      const numbersToLines = (ctx, nums, maxWidth) => {
        if (!Array.isArray(nums) || nums.length === 0) {
          return ["Sem números informados"];
        }

        const parts = nums.map(pad2);
        const lines = [];
        let line = "";

        for (const part of parts) {
          const candidate = line ? `${line}, ${part}` : part;

          if (ctx.measureText(candidate).width <= maxWidth) {
            line = candidate;
          } else {
            if (line) lines.push(line);
            line = part;
          }
        }

        if (line) lines.push(line);

        return lines;
      };

      const chunkArray = (arr, size) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) {
          chunks.push(arr.slice(i, i + size));
        }
        return chunks;
      };

      const data = list
        .map(normalizeBuyer)
        .sort((a, b) =>
          String(a.name || a.email || "").localeCompare(
            String(b.name || b.email || ""),
            "pt-BR",
            { sensitivity: "base" }
          )
        );

      const buildCardItems = () => {
        const items = [];

        data.forEach((buyer, idx) => {
          mctx.font = NAME_FONT;
          let titleLines = wrapSafe(mctx, buyer.name, innerW);

          // Limita somente nome muito grande para preservar layout.
          // Os números não são cortados; quando necessário, são divididos em mais cards.
          if (titleLines.length > 2) {
            titleLines = titleLines.slice(0, 2);
            titleLines[1] = fitSingleLine(mctx, titleLines[1], innerW);
          }

          mctx.font = NUM_FONT;
          const allNumLines = numbersToLines(mctx, buyer.numbers, innerW);
          const numberLineChunks = chunkArray(allNumLines, MAX_NUM_LINES_PER_CARD);
          const totalParts = numberLineChunks.length;

          numberLineChunks.forEach((numLines, partIndex) => {
            const isSplit = totalParts > 1;

            const partLabel = isSplit
              ? `Parte ${partIndex + 1}/${totalParts}`
              : "";

            const h =
              30 +
              titleLines.length * NAME_LH +
              META_LH +
              (partLabel ? 24 : 0) +
              16 +
              numLines.length * NUM_LH +
              34;

            items.push({
              buyerIndex: idx,
              user_id: buyer.user_id,
              name: buyer.name,
              qty: buyer.qty,
              total_cents: buyer.total_cents,
              titleLines,
              numLines,
              partLabel,
              accent: buyer.accent,
              h,
            });
          });
        });

        return items;
      };

      const bg = (ctx) => {
        const g = ctx.createLinearGradient(0, 0, 0, H);
        g.addColorStop(0, "#0b0b0b");
        g.addColorStop(1, "#050505");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      };

      let logoImg = null;

      try {
        const dataURL = await preloadAsDataURL(logoNewStore);
        logoImg = new Image();
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          logoImg.src = dataURL;
        });
      } catch {
        logoImg = null;
      }

      const drawHeader = (ctx) => {
        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";

        if (logoImg) {
          const logoH = 52;
          const scale = logoH / logoImg.height;
          const logoW = logoImg.width * scale;
          ctx.drawImage(logoImg, P, TOP_Y, logoW, logoH);
        } else {
          ctx.fillStyle = "#ffffff";
          ctx.globalAlpha = 0.92;
          ctx.fillRect(P, TOP_Y, 52, 52);
          ctx.globalAlpha = 1;

          ctx.fillStyle = "#0b0b0b";
          ctx.font = "900 34px Inter, system-ui, Arial";
          ctx.textBaseline = "middle";
          ctx.fillText("N", P + 16, TOP_Y + 27);
          ctx.textBaseline = "alphabetic";

          ctx.fillStyle = "#ffffff";
          ctx.font = "800 40px Inter, system-ui, Arial";
          ctx.fillText("NEW STORE", P + 74, TOP_Y + 40);
        }

        ctx.fillStyle = "#ffffff";
        ctx.font = "900 54px Inter, system-ui, Arial";
        ctx.fillText("Lista de Compradores", P, TOP_Y + 125);

        ctx.font = "700 28px Inter, system-ui, Arial";
        ctx.fillStyle = "rgba(255,255,255,.72)";
        ctx.fillText(
          `${isAdditionalMode ? "Sorteio Adicional" : "Sorteio Ativo"} — participantes confirmados`,
          P,
          TOP_Y + 166
        );

        const metaY = TOP_Y + 220;

        const meta = [
          ["Nº Sorteio", String(drawId ?? "-")],
          ["Vendidos", String(sold ?? 0)],
          ["Restantes", String(remaining ?? 0)],
          ["Compradores", String(list.length)],
        ];

        const metaW = 240;

        meta.forEach(([label, value], i) => {
          const x = P + i * metaW;

          ctx.font = "700 24px Inter, system-ui, Arial";
          ctx.fillStyle = "rgba(255,255,255,.62)";
          ctx.fillText(label, x, metaY);

          ctx.font = "900 42px Inter, system-ui, Arial";
          ctx.fillStyle = "#ffffff";
          ctx.fillText(value, x, metaY + 48);
        });
      };

      const drawFooter = (ctx, pageIndex, totalPages) => {
        const footerY = H - 34;

        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";
        ctx.font = "600 23px Inter, system-ui, Arial";
        ctx.fillStyle = "rgba(255,255,255,.72)";
        ctx.fillText(
          `Gerado pela administração • ${stamp} • página ${pageIndex}/${totalPages}`,
          P,
          footerY
        );

        ctx.textAlign = "right";
        ctx.font = "800 23px Inter, system-ui, Arial";
        ctx.fillStyle = "rgba(255,255,255,.88)";
        ctx.fillText("newstore", W - P, footerY);

        ctx.textAlign = "left";
      };

      const drawCard = (ctx, item) => {
        const { x, y, h } = item;

        fillRounded(ctx, x, y, cardW, h, CARD_RADIUS, "#111111");
        strokeRounded(ctx, x, y, cardW, h, CARD_RADIUS, "#2c2c2c", 2);

        ctx.fillStyle = item.accent;
        ctx.beginPath();
        ctx.arc(x + 24, y + 30, 10, 0, Math.PI * 2);
        ctx.fill();

        const innerX = x + innerPadX;
        let cy = y + 42;

        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";

        ctx.fillStyle = "#ffffff";
        ctx.font = NAME_FONT;

        for (const line of item.titleLines) {
          ctx.fillText(line, innerX, cy);
          cy += NAME_LH;
        }

        ctx.font = META_FONT;
        ctx.fillStyle = "rgba(255,255,255,.86)";
        ctx.fillText(`Qtd total: ${item.qty}`, innerX, cy);
        cy += META_LH;

        if (item.partLabel) {
          ctx.font = "800 22px Inter, system-ui, Arial";
          ctx.fillStyle = item.accent;
          ctx.fillText(item.partLabel, innerX, cy);
          cy += 24;
        }

        cy += 8;

        ctx.font = NUM_FONT;
        ctx.fillStyle = "rgba(255,255,255,.94)";

        for (const line of item.numLines) {
          ctx.fillText(line, innerX, cy);
          cy += NUM_LH;
        }
      };

      const cardItems = buildCardItems();

      const pages = [];
      let current = {
        items: [],
        colY: [HEADER_END_Y, HEADER_END_Y],
      };

      for (const item of cardItems) {
        const col = current.colY[0] <= current.colY[1] ? 0 : 1;
        const y = current.colY[col];
        const nextY = y + item.h + V_GAP;

        if (nextY > CONTENT_BOTTOM && current.items.length > 0) {
          pages.push(current);
          current = {
            items: [],
            colY: [HEADER_END_Y, HEADER_END_Y],
          };
        }

        const newCol = current.colY[0] <= current.colY[1] ? 0 : 1;
        const newX = newCol === 0 ? P : P + cardW + COL_GAP;
        const newY = current.colY[newCol];

        current.items.push({
          ...item,
          x: newX,
          y: newY,
        });

        current.colY[newCol] = newY + item.h + V_GAP;
      }

      if (current.items.length > 0) {
        pages.push(current);
      }

      if (pages.length === 0) {
        setExportError("Nenhuma página foi gerada para exportação.");
        return;
      }

      const base = safeFilename(`sorteio_${drawTypeSlug}_${drawId}_lista_1080x1920`);
      const zip = new JSZip();

      for (let p = 0; p < pages.length; p++) {
        const canvas = document.createElement("canvas");
        canvas.width = W;
        canvas.height = H;

        const ctx = canvas.getContext("2d");

        bg(ctx);
        drawHeader(ctx);

        for (const item of pages[p].items) {
          drawCard(ctx, item);
        }

        drawFooter(ctx, p + 1, pages.length);

        const blob = await canvasToBlob(canvas, "image/png");
        zip.file(`${base}_p${String(p + 1).padStart(2, "0")}.png`, blob);
      }

      const csvRows = [];
      csvRows.push(["draw_id", "user_id", "name", "email", "count", "numbers", "total_cents"]);

      data.forEach((b) => {
        csvRows.push([
          drawId,
          b.user_id,
          b.name,
          b.email,
          b.qty,
          b.numbers.map(pad2).join(" "),
          b.total_cents || 0,
        ]);
      });

      const csv = csvRows
        .map((row) =>
          row
            .map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`)
            .join(",")
        )
        .join("\n");

      zip.file(`${base}_compradores.csv`, csv);

      zip.file(
        `${base}_README.txt`,
        [
          `Tipo: ${isAdditionalMode ? "Adicional" : "Principal"}`,
          `Sorteio: ${drawId}`,
          `Total compradores: ${list.length}`,
          `Vendidos: ${sold}`,
          `Restantes: ${remaining}`,
          `Total paginas PNG: ${pages.length}`,
          `Gerado em: ${stamp}`,
          "",
          "Cada PNG possui formato 1080x1920.",
          "Compradores com muitos números podem aparecer em mais de um card como Parte 1/2, Parte 2/2 etc.",
        ].join("\n")
      );

      const zipBlob = await zip.generateAsync({ type: "blob" });
      downloadBlob(zipBlob, `${base}.zip`);
    } catch (e) {
      console.error(e);
      setExportError(e?.message || "Falha ao exportar ZIP com PNGs da lista.");
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <AppBar position="sticky" elevation={0} sx={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
        <Toolbar sx={{ position: "relative", minHeight: 64 }}>
          <IconButton edge="start" color="inherit" onClick={() => navigate("/admin")} aria-label="Voltar">
            <ArrowBackIosNewRoundedIcon />
          </IconButton>

          <Box component={RouterLink} to="/admin"
            sx={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
            <Box component="img" src={logoNewStore} alt="NEW STORE" sx={{ height: 40 }} />
          </Box>

          <IconButton color="inherit" sx={{ ml: "auto" }}>
            <AccountCircleRoundedIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 22, md: 28 }, flex: 1 }}>
              {screenTitle}
            </Typography>
            <Stack spacing={0.75}>
              <Typography variant="caption" sx={{ opacity: 0.72, fontWeight: 800 }}>
                Tipo de sorteio
              </Typography>
              <Tabs
                value={drawMode}
                onChange={(_, value) => setDrawMode(value)}
                textColor="primary"
                indicatorColor="primary"
                sx={{ minHeight: 36 }}
              >
                <Tab value="principal" label="Principal" sx={{ minHeight: 36, fontWeight: 900 }} />
                <Tab value="adicional" label="Adicional" sx={{ minHeight: 36, fontWeight: 900 }} />
              </Tabs>
            </Stack>
          </Stack>

            {isAdditionalMode && additionalDraws.length > 0 && (
              <TextField
                select
                size="small"
                label="Sorteio adicional"
                value={selectedAdditionalDrawId}
                onChange={(event) => setSelectedAdditionalDrawId(event.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 280 } }}
              >
                {additionalDraws.map((item) => (
                  <MenuItem key={item.draw.id} value={String(item.draw.id)}>
                    #{item.draw.id} - {item.draw.product_name || item.draw.banner_title || "Sorteio adicional"}
                    {String(item.draw.status || "").toLowerCase() === "open"
                      ? " — Em andamento"
                      : String(item.draw.status || "").toLowerCase() === "closed"
                      ? " — Encerrado"
                      : ` — ${item.draw.status || "-"}`}
                  </MenuItem>
                ))}
              </TextField>
            )}
          {isAdditionalMode && selectedAdditionalDrawId && (
            <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 700 }}>
              Números e compradores do sorteio adicional #{selectedAdditionalDrawId}.
            </Typography>
          )}
          {emptyMessage && <Alert severity="info">{emptyMessage}</Alert>}
          {loadError && <Alert severity="error">{loadError}</Alert>}

          {!emptyMessage && !loadError && (
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, ...adminPanelPaperSx }}>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <Stack sx={{ mr: 3 }}>
                <Typography sx={{ opacity: .7, fontWeight: 700 }}>Nº Sorteio</Typography>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>{loading ? "…" : (drawId ?? "-")}</Typography>
              </Stack>
              <Stack sx={{ mr: 3 }}>
                <Typography sx={{ opacity: .7, fontWeight: 700 }}>Vendidos (aprovados)</Typography>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>{loading ? "…" : sold}</Typography>
              </Stack>
              <Stack sx={{ mr: 3 }}>
                <Typography sx={{ opacity: .7, fontWeight: 700 }}>Restantes</Typography>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>{loading ? "…" : remaining}</Typography>
              </Stack>

              <Box sx={{ flex: 1 }} />

              <TextField
                size="small"
                placeholder="Buscar por nome, e-mail ou número…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ minWidth: { xs: "100%", sm: 280 } }}
              />

              <Button
                startIcon={<DownloadRoundedIcon />}
                onClick={exportCSV}
                variant="outlined"
                disabled={loading || !drawId}
              >
                Exportar CSV
              </Button>
              <Button
                startIcon={<DownloadRoundedIcon />}
                onClick={exportPNGMobile}
                variant="contained"
                disabled={loading || !drawId}
              >
                Exportar PNG (Grade 1080×1920)
              </Button>
              <Button
                startIcon={<DownloadRoundedIcon />}
                onClick={exportPNGListMobile}
                variant="contained"
                color="primary"
                disabled={loading || !drawId}
              >
                Exportar ZIP (Lista PNGs 1080×1920)
              </Button>
            </Stack>

            {exportError && (
              <Typography sx={{ mt: 2, color: "#ff8a80", fontWeight: 700 }}>
                {exportError}
              </Typography>
            )}

            <Divider sx={{ my: 2.5 }} />

            <Tabs value={tab} onChange={(_, v) => setTab(v)} textColor="primary" indicatorColor="primary" sx={{ mb: 2 }}>
              <Tab label="Por comprador" />
              <Tab label="Por número (00–99)" />
            </Tabs>

            {/* --- Tab 1: Compradores --- */}
            {tab === 0 && (
              <Box sx={{ mt: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>Comprador</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Qtd</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Números</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>Valor (R$)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredBuyers.length === 0 && (
                        <TableRow><TableCell colSpan={5} sx={{ color: "#bbb" }}>Não há compradores para este sorteio.</TableCell></TableRow>
                      )}
                      {filteredBuyers.map((b, i) => (
                        <TableRow key={b.user_id || i}>
                          <TableCell sx={{ fontWeight: 700 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip size="small" label={pad2(i+1)} sx={{ bgcolor: buyerColor(idToIdx.get(b.user_id) ?? i), color: "#000", fontWeight: 800 }} />
                              <span>{b.name || "(sem nome)"}</span>
                            </Stack>
                          </TableCell>

                          <TableCell>{b.count || 0}</TableCell>
                          <TableCell sx={{ maxWidth: 520, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {(b.numbers || []).map(pad2).join(", ")}
                          </TableCell>
                          <TableCell>
                            {((b.total_cents || 0) / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* --- Tab 2: Grade 00–99 --- */}
            {tab === 1 && (
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(10, 1fr)", md: "repeat(20, 1fr)" },
                    gap: .6,
                  }}
                >
                  {gridNumbers.map((n) => {
                    const owner = numbers.find(x => Number(x.n) === n);
                    const idx   = owner ? (idToIdx.get(owner.user_id) ?? 0) : 0;
                    const bg    = owner ? buyerColor(idx) : "transparent";
                    const bd    = owner ? "none" : "1px solid rgba(255,255,255,.18)";
                    const fg    = owner ? "#000" : "inherit";
                    const title = owner ? `${pad2(n)} • ${owner.name || owner.email || "Comprador"}` : pad2(n);
                    return (
                      <Box
                        key={n}
                        title={title}
                        sx={{
                          userSelect: "none",
                          textAlign: "center",
                          py: .8,
                          borderRadius: 1.5,
                          fontWeight: 800,
                          letterSpacing: .5,
                          fontSize: 12,
                          border: bd,
                          bgcolor: bg,
                          color: fg,
                        }}
                      >
                        {pad2(n)}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Paper>
          )}
        </Stack>
      </Container>
    </ThemeProvider>
  );
}
